import React, { FC, ReactElement, useState, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import { clsx } from 'clsx';
// Components
import Space from '../layout/space';
import Text from '../text';
import SvgIcon from '../icons/SvgIcon';
// Resources
import { stringsRes } from '../../resources/strings';
import { capitalizeFirstLetter } from '../../utils/strings';
import { PickerOption } from '../../core/apiTypes';
import { IconCommon } from '../../resources/images';

export const RESET_VALUE = 'reset';

interface EditSelectProps {
    items: PickerOption[];
    title?: string;
    description?: string;
    label?: string;
    name?: string;
    disabled?: boolean;
    initialValue?;
    noReset?: boolean;
    resetLabel?: string;
    noTitle?: boolean; // maybe remove completely the H35 for title
    containerClassName?: string;
    onSelect?: (data) => void;
    onChange?: (e: any) => void;
}

const EditSelect: FC<EditSelectProps> = (props): ReactElement => {
    const {
        title,
        noTitle,
        description,
        label,
        name,
        disabled,
        items,
        initialValue,
        containerClassName,
        onSelect,
        onChange,
        noReset,
        resetLabel,
    } = props;
    const classes = useStyles();

    const [selectedValue, setValue] = useState(initialValue ?? '');
    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        setValue(initialValue ?? '');
    }, [initialValue]);

    const handleChange = (event): void => {
        setValue(event.target.value);
        onSelect?.(event.target.value === RESET_VALUE ? '' : event.target.value);
        onChange?.(event);
    };

    const renderOptions = (): JSX.Element[] => {
        if (items?.length) {
            return items.map((el): JSX.Element => {
                return (
                    <MenuItem value={el.value} key={el.value} className={classes.editSelectItem}>
                        {el.label}
                    </MenuItem>
                );
            });
        }
        return null;
    };

    const onOpen = (): void => {
        setOpen(true);
    };

    const onClose = (): void => {
        setOpen(false);
    };

    return (
        <div className={containerClassName}>
            {!noTitle && title && (
                <div>
                    <Text.H35 text={title} />
                    <Space value={2} />
                    {description && (
                        <>
                            <Text.Description16 text={description} customColor="grey" />
                            <Space value={2} />
                        </>
                    )}
                </div>
            )}
            <FormControl variant="filled" className={clsx(classes.formControl, open && classes.whiteBackground)}>
                {(label || title) && (
                    <InputLabel htmlFor={title ?? 'select-label'}>{capitalizeFirstLetter(label ?? title)}</InputLabel>
                )}
                {/* {label && <InputLabel htmlFor="select-label">{capitalizeFirstLetter(label)}</InputLabel>} */}
                <Select
                    id="select-label"
                    variant="outlined"
                    onOpen={onOpen}
                    onClose={onClose}
                    className={classes.select}
                    value={selectedValue}
                    onChange={handleChange}
                    disabled={disabled}
                    inputProps={{
                        name: name ?? title,
                        id: title ?? 'select-label',
                    }}
                    MenuProps={{
                        className: classes.editSelectContainer,
                    }}
                    IconComponent={(iconProps) =>
                        !disabled && (
                            <div className={`material-icons ${iconProps.className} ${classes.icon}`}>
                                <SvgIcon icon={IconCommon.ChevronDown} color="black" fill="none" />
                            </div>
                        )
                    }
                >
                    {renderOptions()}
                    {!noReset && (
                        <MenuItem aria-label="None" value={RESET_VALUE} className={classes.editSelectItem}>
                            {resetLabel ?? stringsRes.common.reset}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        select: {
            backgroundColor: theme.palette.colors.background.primaryElement,
            height: 56,
        },
        editSelectContainer: {
            '& .MuiPaper-root': {
                background: theme.palette.colors.background.appBar,
            },
        },
        icon: {
            width: 20,
            height: 20,
            right: 16,
        },
        formControl: {
            width: '100%',
            border: `1.5px solid transparent`,
            '& .MuiOutlinedInput-input': {
                fontWeight: 500,
                padding: '27px 16px 10px',
                paddingRight: '40px !important',
                backgroundColor: 'unset',
            },
            '& .MuiOutlinedInput-input.Mui-disabled': {
                paddingRight: 16,
                color: theme.palette.colors.text.label,
            },
            '& .MuiSelect-iconFilled': {
                marginRight: 16,
            },
            '& .MuiOutlinedInput-root': {
                borderRadius: 6,
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderWidth: 1.5,
                borderColor: 'transparent',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline ': {
                borderWidth: 1.5,
                borderColor: 'transparent',
            },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline ': {
                borderWidth: 1.5,
                borderColor: 'transparent',
            },
            '& .MuiFormLabel-root': {
                fontSize: 16,
                fontWeight: 500,
                marginLeft: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap!important',
            },
            '& .MuiFormLabel-root.Mui-focused': {
                color: 'theme.palette.colors.text.label',
            },
        },
        whiteBackground: {
            borderRadius: 6,
            background: theme.palette.colors.background.appBar,
            border: `1.5px solid ${theme.palette.secondary.main}`,
            '& .MuiFormLabel-root.Mui-focused': {
                color: theme.palette.secondary.main,
            },
        },
        editSelectItem: {
            background: theme.palette.colors.background.appBar,
            fontSize: 16,
            fontWeight: 400,
            padding: '12px 16px',
        },
    }),
);

export default EditSelect;
